// toast notifications
import { toast } from "react-toastify";

// @mui material components
import Card from "@mui/material/Card";
import Grid from "@mui/material/Grid";

// Material Dashboard 2 PRO React TS components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";

// Forms components
import { Formik, Form } from "formik";
import FormField from "../FormField";
import { form, initialValues, validations } from "./schemas/form";

// Data and functions
import { useUserContext } from "contexts/contextUser";
import { useUpdatePassword } from "api/features/auth/queries";

function ChangePassword(): JSX.Element {
  const { formId, formField } = form;
  const { currentPassword, newPassword, repeatNewPassword } = formField;

  const { user, setSession } = useUserContext();

  const setUserPasswordClient = useUpdatePassword();

  const handleSubmit = async (values: any, actions: any) => {
    setUserPasswordClient.mutate(
      {
        userId: user?.user_id,
        newPassword: values.newPassword,
      },
      {
        onSuccess: (data) => {
          setSession(data);
          toast.success("Password updated successfully");
          actions.setSubmitting(false);
          actions.resetForm();
        },
        onError: (error: any) => {
          toast.error(`Error updating password: ${error.message}`);
          actions.setSubmitting(false);
        },
      }
    );
  };

  const passwordRequirements = [
    "Min 1 special character (!@#$%^&*)",
    "Min 1 number (0-9)",
    "Min 1 uppercase letter",
    "Min 1 lowercase letter",
    "Min 6 characters",
  ];

  const renderPasswordRequirements = passwordRequirements.map((item, key) => {
    const itemKey = `element-${key}`;

    return (
      <MDBox key={itemKey} component="li" color="text" fontSize="1.25rem" lineHeight={1}>
        <MDTypography variant="button" color="text" fontWeight="regular" verticalAlign="middle">
          {item}
        </MDTypography>
      </MDBox>
    );
  });

  return (
    <Card id="change-password">
      <MDBox p={3}>
        <MDTypography variant="h5">Change Password</MDTypography>
      </MDBox>
      <Formik initialValues={initialValues} validationSchema={validations} onSubmit={handleSubmit}>
        {({ values, errors, touched, isSubmitting }) => (
          <Form id={formId} autoComplete="off">
            <MDBox pb={3} px={3}>
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <FormField
                    type={currentPassword.type}
                    label={currentPassword.label}
                    name={currentPassword.name}
                    value={values.currentPassword}
                    error={errors.password && touched.password}
                    success={values.currentPassword.length > 0 && !errors.password}
                    inputProps={{ autoComplete: "" }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <FormField
                    type={newPassword.type}
                    label={newPassword.label}
                    name={newPassword.name}
                    value={values.newPassword}
                    error={errors.newPassword && touched.newPassword}
                    success={values.newPassword.length > 0 && !errors.newPassword}
                    inputProps={{ autoComplete: "" }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <FormField
                    type={repeatNewPassword.type}
                    label={repeatNewPassword.label}
                    name={repeatNewPassword.name}
                    value={values.repeatNewPassword}
                    error={errors.repeatNewPassword && touched.repeatNewPassword}
                    success={values.repeatNewPassword.length > 0 && !errors.repeatNewPassword}
                    inputProps={{ autoComplete: "" }}
                  />
                </Grid>
              </Grid>
              <MDBox mt={6} mb={1}>
                <MDTypography variant="h5">Password requirements</MDTypography>
              </MDBox>
              <MDBox mb={1}>
                <MDTypography variant="body2" color="text">
                  Please follow this guide for a strong password
                </MDTypography>
              </MDBox>
              <MDBox
                display="flex"
                justifyContent="space-between"
                alignItems="flex-end"
                flexWrap="wrap"
              >
                <MDBox component="ul" m={0} pl={3.25} mb={{ xs: 8, sm: 0 }}>
                  {renderPasswordRequirements}
                </MDBox>
                <MDBox ml="auto">
                  <MDButton
                    variant="gradient"
                    color="dark"
                    size="small"
                    disabled={
                      isSubmitting ||
                      Object.keys(errors).length > 0 ||
                      Object.keys(touched).length == 0
                    }
                    type="submit"
                  >
                    update password
                  </MDButton>
                </MDBox>
              </MDBox>
            </MDBox>
          </Form>
        )}
      </Formik>
    </Card>
  );
}

export default ChangePassword;
